<template>
  <form class="row contact-form" :class="{ 'contact-form--mobile': isMobile }" :id="formId">
    <fieldset
      v-for="[inputName, inputField] in filterInputList"
      :key="inputName"
      :form="formId"
      class="col-12 g-3"
    >
      <component
        :is="inputField.component"
        :name="inputName"
        :attribute-name="inputField.placeholder"
        :placeholder="inputField.placeholder"
        :required="inputField.required"
        :form="formId"
        :autocomplete="inputField.autocomplete"
      />
    </fieldset>
    <div v-if="appointmentErrors.length" class="alert alert-warning my-5">
      <p class="mb-3">{{ $t('submission_form.form.validation_message') }}</p>
      <ul class="clean-ul">
        <li v-for="(error, index) of appointmentErrors" :key="index" class="pt-2mb-3">
          {{ error }}
        </li>
      </ul>
    </div>
  </form>
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent, ComputedRef, reactive } from 'vue'
import { ContactForm } from '~concierge/types/FormTypes'
import { storeToRefs } from 'pinia'
import { wTrans } from 'laravel-vue-i18n'
import { useSubmissionFormStore } from '~conciergeUiCommons/store/submissionFormStore'
import { useIdentityStore } from '~conciergeUiCommons/store/identityStore'
import { useMobileDetection } from '~conciergeUiCommons/composables/deviceDetection'
import { IdentitySettings } from '~concierge/types/IdentityTypes'

interface InputField {
  required: boolean
  placeholder: string | ComputedRef<string>
  component: string
  autocomplete: string
}

export default defineComponent({
  name: 'ContactForm',
  setup(props) {
    const submissionFormStore = useSubmissionFormStore()
    const { appointmentErrors } = storeToRefs(submissionFormStore)
    const identityStore = useIdentityStore()
    const settings = identityStore.identity?.settings as IdentitySettings
    const customerCommentEnabled = settings?.customerCommentEnabled ?? false
    const dealerName = identityStore.identity?.contact?.organization
    const formId = props.formId || 'section-concierge-contact-' + Math.random().toString(36)

    if (!customerCommentEnabled) {
      submissionFormStore.removeCommentField()
    }

    const { isMobile } = useMobileDetection()
    return { appointmentErrors, dealerName, formId, isMobile, settings, customerCommentEnabled }
  },
  props: {
    formId: {
      type: String,
      default: ''
    }
  },
  components: {
    PhoneInput: defineAsyncComponent(
      () => import('~conciergeUiCommons/components/ContactForm/PhoneInput.vue')
    ),
    EmailInput: defineAsyncComponent(
      () => import('~conciergeUiCommons/components/ContactForm/EmailInput.vue')
    ),
    FirstNameInput: defineAsyncComponent(
      () => import('~conciergeUiCommons/components/ContactForm/FirstNameInput.vue')
    ),
    LastNameInput: defineAsyncComponent(
      () => import('~conciergeUiCommons/components/ContactForm/LastNameInput.vue')
    ),
    CommentInput: defineAsyncComponent(
      () => import('~conciergeUiCommons/components/ContactForm/CommentInput.vue')
    ),
    ConsentInput: defineAsyncComponent(
      () => import('~conciergeUiCommons/components/ContactForm/ConsentInput.vue')
    )
  },
  data() {
    return {
      inputList: reactive<Map<keyof ContactForm, InputField>>(
        new Map([
          [
            'first_name',
            {
              required: true,
              placeholder: wTrans('submission_form.form.fields.first_name'),
              component: 'FirstNameInput',
              autocomplete: `${this.formId} given-name`
            }
          ],
          [
            'last_name',
            {
              required: true,
              placeholder: wTrans('submission_form.form.fields.last_name'),
              component: 'LastNameInput',
              autocomplete: `${this.formId} family-name`
            }
          ],
          [
            'cellphone',
            {
              required: true,
              placeholder: wTrans('submission_form.form.fields.cellphone'),
              component: 'PhoneInput',
              autocomplete: `${this.formId} tel`
            }
          ],
          [
            'email',
            {
              required: false,
              placeholder: wTrans('submission_form.form.fields.email'),
              component: 'EmailInput',
              autocomplete: `${this.formId} email`
            }
          ],
          [
            'comment',
            {
              required: false,
              placeholder: wTrans('submission_form.comment_placeholder'),
              component: 'CommentInput',
              autocomplete: `${this.formId} off`
            }
          ],
          [
            'consent',
            {
              required: true,
              placeholder: wTrans('submission_form.form.fields.consent', {
                dealer_name: this.dealerName
              }),
              component: 'ConsentInput',
              autocomplete: `${this.formId} off`
            }
          ]
        ])
      )
    }
  },
  computed: {
    filterInputList(): Map<keyof ContactForm, InputField> {
      const list = new Map(this.inputList)

      if (!this.customerCommentEnabled) {
        list.delete('comment')
      }

      return list
    }
  }
})
</script>
<style lang="scss">
.contact-form {
  input[type='text'],
  input[type='email'],
  input[type='tel'] {
    font-size: 1.72rem;
  }
}

.contact-form--mobile {
  input[type='text'],
  input[type='email'],
  input[type='tel'] {
    font-size: 16px !important;
    padding: 0.4em 0.55em;

    &::-webkit-input-placeholder {
      line-height: normal !important;
      font-size: 16px !important;
      padding: 0.4em 0.55em;
    }
  }
}
</style>
